import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../supabaseClient';
import { CATEGORY_TO_TRADE, buildTradeLookup, fetchTradeCareerData, fmtCurrency, shortLocation } from '../utils/tradePayData';
import { useSavedResources } from '../utils/savedResources';
import { fetchPublishedLocalNames, wageLinkForDirectoryName } from '../utils/unionWages';
import useIsPhone from '../utils/useIsPhone';

const NAVY      = '#0D1B2A';
const NAVY_MID  = '#132233';
const AMBER     = '#F5A623';
const WHITE     = '#FFFFFF';
const FONT_BODY = "'DM Sans', sans-serif";

// One labelled detail line on a card. Plain-text labels (no emoji) keep every line aligned
// the same way; value may be a string or a node.
const lineLabel = { flexShrink: 0, fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.38)', paddingTop: '2px', minWidth: '78px' };
function DetailLine({ label, children, color = 'rgba(255,255,255,0.55)', mb = '6px' }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '0.78rem', color, marginBottom: mb, lineHeight: 1.4 }}>
      <span style={lineLabel}>{label}</span><span style={{ minWidth: 0 }}>{children}</span>
    </div>
  );
}

// Phones: a 44px-tall tap target with 14px text for links and buttons on a card.
const PHONE_TAP = { minHeight: '44px', fontSize: '0.875rem', alignItems: 'center' };

// Tuition amounts are per-credit-hour rates and are not always whole dollars (they run from
// $116 to $591.71), so unlike fmtCurrency -- which rounds annual salaries to whole dollars --
// this keeps cents when a rate has them and drops them when it doesn't.
const fmtTuition = n =>
  Number(n).toLocaleString('en-US', {
    style: 'currency', currency: 'USD',
    minimumFractionDigits: 0, maximumFractionDigits: 2,
  });

// Resolves a card's tuition line, or null when there's nothing real to show.
// Two genuinely different shapes live in this table:
//  - Community colleges carry per-credit-hour rates split by residency. `tuition_in_district`
//    is never null while the other two are populated, so it gates the whole branch. Any of the
//    residency tiers can be missing on its own (16 rows have no out-of-state rate), so segments
//    are collected and joined rather than templated -- the line just ends after the last real
//    figure instead of trailing a "$null"/"N/A".
//  - Flat-rate schools have no per-credit split at all, only the human-readable `cost` string
//    ("$41,900/year full-time ..."), which is already display-ready.
// A row with neither renders no line at all, matching how the pay snippet stays absent rather
// than printing a placeholder when wage data is missing.
function tuitionOf(r, t) {
  if (r.tuition_in_district != null) {
    const segments = [
      [r.tuition_in_district, 'resources.card.tuitionInDistrict'],
      [r.tuition_out_of_district, 'resources.card.tuitionOutOfDistrict'],
      [r.tuition_out_of_state, 'resources.card.tuitionOutOfState'],
    ]
      .filter(([amount]) => amount != null)
      .map(([amount, key]) => t(key, { amount: fmtTuition(amount) }));
    if (!segments.length) return null;
    // tuition_unit is free text from the import ("per credit hour"). Translate the units we
    // actually see, and fall back to showing the raw value rather than dropping it, so a new
    // unit added upstream still renders truthfully in English.
    const unitKey = {
      'per credit hour': 'resources.card.tuitionUnitPerCreditHour',
      'per semester hour': 'resources.card.tuitionUnitPerSemesterHour',
    }[(r.tuition_unit || '').trim().toLowerCase()];
    const unit = unitKey ? t(unitKey) : (r.tuition_unit || '').trim();
    const joined = segments.join(' · ');
    return unit ? t('resources.card.tuitionWithUnit', { tuition: joined, unit }) : joined;
  }
  if (r.cost_min != null || r.cost_max != null) return r.cost || null;
  return null;
}

// Renders a flat list of union cards, or a college-grouped collapsible list of school cards
// (collapsed by default, keyed by institution name -- same "reduce into groups" pattern as
// EmployerDashboard's Candidates-by-job-posting grouping). Shared by ResourceFinder (the full
// browse experience, with its own search/filters) and TradeHub (a pre-filtered single-trade
// view), so both surfaces render identical cards and grouping behavior.
//
// hasActiveFilter: true forces every group open (used by ResourceFinder while a search/filter
// is narrowing results, so a match is never hidden inside a collapsed institution).
// defaultExpanded: initial expand state for a group the user hasn't explicitly toggled.
// Defaults to false everywhere -- a trade with many matching colleges (Welding, HVAC, etc.)
// would otherwise render every program list open at once, ballooning the page height. An
// "Expand all" control (below, shown only when there's more than one group) covers the case
// where a caller like TradeHub still wants everything visible in one click.
export default function ResourceCardList({ resources, type, onViewTradePay, hasActiveFilter = false, defaultExpanded = false }) {
  const { t } = useTranslation();
  const [userId, setUserId] = useState(null);
  const [payLookup, setPayLookup] = useState(new Map());
  const [expandedGroups, setExpandedGroups] = useState({});
  const { isSaved, toggle: toggleSaved } = useSavedResources();
  // Locals with published union wage rates, for the "See published wages" link on union cards.
  // A failed read just means no links -- the cards themselves don't depend on it.
  const [wageLocals, setWageLocals] = useState(new Set());
  const isPhone = useIsPhone();

  useEffect(() => {
    if (type !== 'union') return;
    let cancelled = false;
    fetchPublishedLocalNames().then(res => { if (!cancelled && !res.error) setWageLocals(res.names); });
    return () => { cancelled = true; };
  }, [type]);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setUserId(data?.user?.id || null));
  }, []);

  // Small (33-row) table -- fetched once and matched client-side against each card's
  // category via the curated CATEGORY_TO_TRADE map, rather than re-querying per card.
  useEffect(() => {
    fetchTradeCareerData().then(rows => setPayLookup(buildTradeLookup(rows)));
  }, []);

  // Fire the click log first (not awaited -- never blocks the outbound tab).
  const trackClick = (resource) => {
    supabase.from('resource_clicks').insert({ resource_id: resource.id, user_id: userId }).then(() => {}, () => {});
  };
  const handleVisit = (resource) => {
    trackClick(resource);
    // Granular per-program rows should open the specific program's catalog page, not the
    // school's generic homepage -- falls back to website_url if a program row is missing
    // its catalog_url rather than leaving the button dead.
    const url = (resource.program_name && resource.catalog_url) ? resource.catalog_url : resource.website_url;
    window.open(url, '_blank', 'noopener,noreferrer');
  };
  const handleContact = (resource) => {
    trackClick(resource);
  };

  // Card pieces shared by the school card and the union card.
  const renderHeader = (r, saved) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px', marginBottom: '8px' }}>
      <p style={{ fontWeight: 700, fontSize: '0.93rem', color: WHITE, lineHeight: 1.3, flex: 1 }}>{r.name}</p>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
        {/* Corner badge answers "what kind of institution is this" -- school_type for
            schools (falling back to "Trade School" since older/incomplete rows may not
            have it set), category (the trade taught) for unions, unchanged. */}
        {(type === 'school' ? (r.school_type || t('resources.card.tradeSchoolFallback')) : r.category) && (
          <span style={{
            backgroundColor: 'transparent', color: WHITE,
            border: '1px solid rgba(255,255,255,0.28)',
            borderRadius: '999px', fontSize: '0.62rem', fontWeight: 700, padding: '3px 9px',
            whiteSpace: 'nowrap', flexShrink: 0,
          }}>
            {type === 'school' ? (r.school_type || t('resources.card.tradeSchoolFallback')) : r.category}
          </span>
        )}
        {/* Save toggle -- localStorage only, no account needed. Stores the full resource
            snapshot (not just the id) -- SavedResourcesPanel re-verifies current active
            status against resource_directory before trusting the snapshot. */}
        <button
          onClick={() => toggleSaved({ ...r, type })}
          aria-label={saved ? t('resources.card.removeFromSaved') : t('resources.card.saveForComparison')}
          title={saved ? t('resources.card.removeFromSaved') : t('resources.card.saveForComparison')}
          style={{
            background: 'none', border: 'none', padding: 0, cursor: 'pointer',
            fontSize: '1.15rem', lineHeight: 1, color: saved ? AMBER : 'rgba(255,255,255,0.32)',
            // Phones: a 44x44 target; the negative margin keeps the card header the same height.
            ...(isPhone ? {
              display: 'flex', alignItems: 'center', justifyContent: 'center', width: '44px', height: '44px',
              margin: '-12px -12px -12px -6px', fontSize: '1.3rem', color: saved ? AMBER : 'rgba(255,255,255,0.5)',
            } : {}),
          }}
        >
          {saved ? '★' : '☆'}
        </button>
      </div>
    </div>
  );
  const renderActions = (r) => (
    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: 'auto' }}>
      <button
        onClick={() => handleVisit(r)}
        style={{
          display: 'inline-block', backgroundColor: AMBER, color: NAVY, fontWeight: 700,
          fontSize: '0.82rem', border: 'none', borderRadius: '8px', padding: '8px 14px',
          cursor: 'pointer', fontFamily: FONT_BODY,
          ...(isPhone ? { ...PHONE_TAP, display: 'inline-flex', padding: '0 16px' } : {}),
        }}
      >
        {t('common.visitWebsite')}
      </button>
      {(r.contact_email || r.contact_phone) && (
        <a
          href={r.contact_email ? `mailto:${r.contact_email}` : `tel:${r.contact_phone}`}
          onClick={() => handleContact(r)}
          style={{
            display: 'inline-block', backgroundColor: NAVY, color: WHITE,
            border: '1px solid rgba(255,255,255,0.25)', fontWeight: 700, fontSize: '0.82rem',
            textDecoration: 'none', borderRadius: '8px', padding: '8px 14px', fontFamily: FONT_BODY,
            ...(isPhone ? { ...PHONE_TAP, display: 'inline-flex', padding: '0 16px' } : {}),
          }}
        >
          {t('resources.card.contact')}
        </a>
      )}
    </div>
  );

  // Union cards: every card has the same slots in the same order -- name + trade badge,
  // description, training line, address, then links and buttons. A slot with no data is left
  // out rather than padded, so the order never changes between cards.
  const renderUnionCard = (r) => {
    const mappedTrade = r.category ? CATEGORY_TO_TRADE[r.category] : null;
    const payRow = mappedTrade ? payLookup.get(mappedTrade) : null;
    const payMedian = payRow ? fmtCurrency(payRow.median_pay) : null;
    const saved = isSaved(r.id);
    const training = [
      r.programs && r.programs.length > 0 ? r.programs.join(', ') : null,
      r.duration || null,
      r.certifications && r.certifications.length > 0 ? r.certifications.join(', ') : null,
    ].filter(Boolean).join(' · ');
    const wageLink = wageLinkForDirectoryName(r.name, wageLocals);
    return (
      <div key={r.id} data-union-card style={{ backgroundColor: NAVY_MID, border: '1px solid rgba(255,255,255,0.07)', borderRadius: '12px', padding: '16px', display: 'flex', flexDirection: 'column' }}>
        {renderHeader(r, saved)}
        {r.description && (
          <p style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.5)', lineHeight: 1.5, marginBottom: '10px' }}>{r.description}</p>
        )}
        {training && <DetailLine label={t('resources.card.labelTraining')}>{training}</DetailLine>}
        {(r.address || r.city) && (
          <DetailLine label={t('resources.card.labelAddress')} color="rgba(255,255,255,0.45)">{r.address || r.city}</DetailLine>
        )}
        {(wageLink || payMedian) && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '6px', marginTop: '6px' }}>
            {wageLink && (
              <Link
                to={wageLink}
                data-wages-link
                style={{
                  fontSize: '0.78rem', fontWeight: 700, color: AMBER, textDecoration: 'underline', textUnderlineOffset: '2px', lineHeight: 1.4,
                  // Phones: a 44px-tall tap target and 14px text.
                  ...(isPhone ? { display: 'inline-flex', alignItems: 'center', minHeight: '44px', fontSize: '0.875rem' } : {}),
                }}
              >
                {t('resources.card.seePublishedWages')}
              </Link>
            )}
            {payMedian && (
              <button
                onClick={() => onViewTradePay?.(mappedTrade)}
                disabled={!onViewTradePay}
                style={{
                  display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.78rem',
                  color: AMBER, fontWeight: 600, marginBottom: 0, lineHeight: 1.4,
                  background: 'none', border: 'none', padding: 0, textAlign: 'left', fontFamily: FONT_BODY,
                  cursor: onViewTradePay ? 'pointer' : 'default',
                  textDecoration: onViewTradePay ? 'underline' : 'none', textUnderlineOffset: '2px',
                  ...(isPhone ? PHONE_TAP : {}),
                }}
              >
                <span>
                  {t('resources.card.payMedianSnippetUnion', { category: r.category, pay: payMedian, location: shortLocation(payRow) })}
                </span>
              </button>
            )}
          </div>
        )}
        <div style={{ marginTop: '12px' }}>
          {renderActions(r)}
        </div>
      </div>
    );
  };

  // Renders a single school card, inside each expanded institution group.
  const renderCard = (r) => {
    // Inline pay snippet: only shown when the card's category maps to a trade we
    // actually have wage data for (see CATEGORY_TO_TRADE) -- no match means no
    // snippet, never a fabricated or overly-generic estimate.
    const mappedTrade = r.category ? CATEGORY_TO_TRADE[r.category] : null;
    const payRow = mappedTrade ? payLookup.get(mappedTrade) : null;
    const payMedian = payRow ? fmtCurrency(payRow.median_pay) : null;
    const tuition = tuitionOf(r, t);
    const saved = isSaved(r.id);

    return (
      <div key={r.id} style={{ backgroundColor: NAVY_MID, border: '1px solid rgba(255,255,255,0.07)', borderRadius: '12px', padding: '16px', display: 'flex', flexDirection: 'column' }}>
        {renderHeader(r, saved)}
        {type === 'school' && r.category && (
          <span style={{
            display: 'inline-block', backgroundColor: 'rgba(245,166,35,0.12)', color: AMBER,
            border: '1px solid rgba(245,166,35,0.35)',
            borderRadius: '999px', fontSize: '0.62rem', fontWeight: 700, padding: '3px 9px',
            marginBottom: '10px', alignSelf: 'flex-start',
          }}>
            {r.category}
          </span>
        )}
        {payMedian && (
          <button
            onClick={() => onViewTradePay?.(mappedTrade)}
            disabled={!onViewTradePay}
            style={{
              display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.78rem',
              color: AMBER, fontWeight: 600, marginBottom: '10px', lineHeight: 1.4,
              background: 'none', border: 'none', padding: 0, textAlign: 'left', fontFamily: FONT_BODY,
              cursor: onViewTradePay ? 'pointer' : 'default',
              textDecoration: onViewTradePay ? 'underline' : 'none', textUnderlineOffset: '2px',
              ...(isPhone ? { ...PHONE_TAP, marginBottom: '4px' } : {}),
            }}
          >
            <span>
              {t(type === 'school' ? 'resources.card.payMedianSnippetSchool' : 'resources.card.payMedianSnippetUnion', {
                category: r.category, pay: payMedian, location: shortLocation(payRow),
              })}
            </span>
          </button>
        )}
        {tuition && (
          <div style={{ fontWeight: 600 }}>
            <DetailLine label={t('resources.card.labelTuition')} color={AMBER} mb="10px">{tuition}</DetailLine>
          </div>
        )}
        {/* Granular per-program rows (program_name present) -- one specific program per
            card, e.g. a single College of DuPage HVAC certificate, as opposed to the
            coarser rows below (programs[] + duration + cost text) that summarize a
            whole category at that school in one card. */}
        {r.program_name && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: r.credits_min != null ? '4px' : '10px' }}>
            <p style={{ fontSize: '0.84rem', fontWeight: 700, color: AMBER, lineHeight: 1.4 }}>{r.program_name}</p>
            {r.degree_type && (
              <span style={{
                backgroundColor: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.75)',
                border: '1px solid rgba(255,255,255,0.2)', borderRadius: '999px',
                fontSize: '0.6rem', fontWeight: 700, padding: '2px 8px', whiteSpace: 'nowrap', flexShrink: 0,
              }}>
                {r.degree_type}
              </span>
            )}
          </div>
        )}
        {r.program_name && r.credits_min != null && (
          <p style={{ fontSize: '0.76rem', color: 'rgba(255,255,255,0.5)', marginBottom: '10px' }}>{t('resources.card.creditsMinimum', { count: r.credits_min })}</p>
        )}
        {r.description && (
          <p style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.5)', lineHeight: 1.5, marginBottom: '12px' }}>{r.description}</p>
        )}
        {r.programs && r.programs.length > 0 && (
          <DetailLine label={t('resources.card.labelPrograms')}>{r.programs.join(', ')}</DetailLine>
        )}
        {r.duration && (
          <DetailLine label={t('resources.card.labelLength')}>{r.duration}</DetailLine>
        )}
        {/* Suppressed once the tuition line above is showing -- otherwise flat-rate schools
            print the identical `cost` string twice, and the four community-college rows that
            carry both would contradict themselves (Daley College's stale cost text says
            "$146/credit hour" beside a current tuition_in_district of $163). */}
        {r.cost && !tuition && (
          <DetailLine label={t('resources.card.labelCost')}>{r.cost}</DetailLine>
        )}
        {r.tuition_url && (
          <a
            href={r.tuition_url}
            target="_blank"
            rel="noreferrer"
            onClick={() => trackClick(r)}
            style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.78rem', color: AMBER, fontWeight: 600, marginBottom: '6px', lineHeight: 1.4, textDecoration: 'none',
              ...(isPhone ? { ...PHONE_TAP, alignSelf: 'flex-start', marginBottom: '2px' } : {}) }}
          >
            <span>{t('resources.card.tuitionFees')}</span>
          </a>
        )}
        {r.certifications && r.certifications.length > 0 && (
          <DetailLine label={t('resources.card.labelCertifications')}>{r.certifications.join(', ')}</DetailLine>
        )}
        {(r.address || r.city) && (
          <DetailLine label={t('resources.card.labelAddress')} color="rgba(255,255,255,0.45)" mb="12px">{r.address || r.city}</DetailLine>
        )}
        {renderActions(r)}
      </div>
    );
  };

  if (type === 'union') {
    return <>{resources.map(r => renderUnionCard(r))}</>;
  }

  // School cards grouped by institution name, preserving the order institutions first
  // appear in `resources`.
  const groupedSchools = [];
  for (const r of resources) {
    let group = groupedSchools.find(g => g.name === r.name);
    if (!group) { group = { name: r.name, resources: [] }; groupedSchools.push(group); }
    group.resources.push(r);
  }

  const isExpandedFor = (name) => {
    const explicit = Object.prototype.hasOwnProperty.call(expandedGroups, name);
    return hasActiveFilter || (explicit ? expandedGroups[name] : defaultExpanded);
  };
  const allExpanded = groupedSchools.length > 0 && groupedSchools.every(g => isExpandedFor(g.name));

  // A trade with many matching colleges (e.g. Welding, HVAC) would otherwise render every
  // program list open at once if a caller wants groups to start expanded -- this button lets
  // that still be reached in one click without forcing it by default (see defaultExpanded
  // callers: both now pass false/omit it entirely for exactly this reason).
  const toggleAll = () => {
    const next = {};
    for (const g of groupedSchools) next[g.name] = !allExpanded;
    setExpandedGroups(prev => ({ ...prev, ...next }));
  };

  return (
    <>
      {!hasActiveFilter && groupedSchools.length > 1 && (
        <button
          onClick={toggleAll}
          style={{
            alignSelf: 'flex-start', background: 'none', border: 'none', padding: '0 0 2px',
            color: AMBER, fontSize: '0.76rem', fontWeight: 700, cursor: 'pointer', fontFamily: FONT_BODY,
            textDecoration: 'underline', textUnderlineOffset: '2px',
            ...(isPhone ? { ...PHONE_TAP, display: 'inline-flex', padding: '0 2px' } : {}),
          }}
        >
          {allExpanded ? t('resources.card.collapseAll') : t('resources.card.expandAll')}
        </button>
      )}
      {groupedSchools.map(group => {
        const isExpanded = isExpandedFor(group.name);
        return (
          <div key={group.name} style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px', overflow: 'hidden' }}>
            <button
              onClick={() => setExpandedGroups(prev => ({ ...prev, [group.name]: !isExpanded }))}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                background: 'rgba(255,255,255,0.03)', border: 'none', cursor: 'pointer',
                padding: '13px 14px', fontFamily: FONT_BODY, textAlign: 'left',
                ...(isPhone ? { minHeight: '52px', padding: '12px 14px' } : {}),
              }}
            >
              <span style={{ fontSize: isPhone ? '0.875rem' : '13px', fontWeight: 700, color: WHITE, ...(isPhone ? { lineHeight: 1.35 } : {}) }}>
                {group.name} · {t('resources.programCount', { count: group.resources.length })}
              </span>
              <span style={{ fontSize: isPhone ? '12px' : '11px', color: AMBER, fontWeight: 700, flexShrink: 0, marginLeft: '10px' }}>
                {isExpanded ? '▲' : '▼'}
              </span>
            </button>
            {isExpanded && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '12px' }}>
                {group.resources.map(r => renderCard(r))}
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}
